import { useState, useCallback } from 'react';
import { toast } from '../components/toast';
import { useLoadingState } from './useLoadingState';
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  createEmployeeUserAccount,
} from '../services/api';

export const useEmployeeOperations = () => {
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({
    total_employees: 0,
    active_employees: 0,
    inactive_employees: 0,
    departments_count: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});

  const loadEmployees = useCallback(
    async (
      page,
      rowsPerPage,
      searchTerm,
      departmentFilter,
      designationFilter,
      statusFilter,
      startDate,
      endDate,
      sortBy = '',
      sortDirection = 'asc'
    ) => {
      await withLoading(async () => {
        try {
          const statusParam = statusFilter === 'All' ? '' : statusFilter;
          const response = await getEmployees(
            page + 1, // API expects 1-based pagination
            rowsPerPage,
            searchTerm,
            departmentFilter,
            designationFilter,
            statusParam,
            startDate,
            endDate,
            sortBy,
            sortDirection
          );

          if (response.data) {
            setEmployees(response.data.results || []);
            setTotalCount(response.data.pagination_info?.count || 0);

            // Update stats if included in response
            if (response.data.stats) {
              setStats(response.data.stats);
            }

            // Update filter options if included in response
            if (response.data.filter_options) {
              setFilterOptions(response.data.filter_options);
            }
          }
        } catch (error) {
          console.error('Error loading employees:', error);
          toast.error('Failed to load employees');
        }
      });
    },
    [withLoading]
  );

  const createEmployeeRecord = useCallback(async (employeeData) => {
    try {
      await createEmployee(employeeData);
      toast.success('Employee created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error(error.response?.data?.message || 'Failed to create employee');
      return false;
    }
  }, []);

  const updateEmployeeRecord = useCallback(async (employeeId, employeeData) => {
    try {
      await updateEmployee(employeeId, employeeData);
      toast.success('Employee updated successfully!');
      return true;
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error(error.response?.data?.message || 'Failed to update employee');
      return false;
    }
  }, []);

  const deleteEmployeeRecord = useCallback(async (employeeId) => {
    try {
      await deleteEmployee(employeeId);
      toast.success('Employee deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error(error.response?.data?.message || 'Failed to delete employee');
      return false;
    }
  }, []);

  const createUserAccount = useCallback(async (userData) => {
    try {
      await createEmployeeUserAccount(userData);
      toast.success('User account created successfully');
      return true;
    } catch (error) {
      console.error('Error creating user account:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || 'Failed to create user account');
      }
      return false;
    }
  }, []);

  return {
    isLoading,
    employees,
    stats,
    totalCount,
    filterOptions,
    loadEmployees,
    createEmployeeRecord,
    updateEmployeeRecord,
    deleteEmployeeRecord,
    createUserAccount,
  };
};
